/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DirectRoutePage.tsx
 🗓️ Created: 2025-01-21
 🧩 Description:
    Main page for Direct Route management with list view.
    Displays direct routes that override dialrule restrictions.

 🔧 Notes:
    - Domain-scoped data
    - Supports pagination and search
    - Includes delete confirmation

 🔁 Last Updated: 2025-01-21 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { useContextStore } from '../../store/context';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { deleteDirectRouteById, navigateToDirectRouteDuplicate } from './directRouteActions';
import { useDirectRouteList } from './hooks/useDirectRouteList';

export interface DirectRoute {
  id: number;
  uuid: string;
  domainId: number;
  name: string;
  description: string;
  context: string;
  numberToDial: string;
  classOfServicesId: number;
  classOfService?: { name: string };
  callerIdName?: string;
  callerIdNumber?: string;
  enabled: boolean;
  createdAt: string;
  createdBy?: number;
  updatedAt?: string;
  updatedBy?: number;
}

export default function DirectRoutePage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; route: DirectRoute | null }>({
    isOpen: false,
    route: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Generate columns
  const availableColumns = useMemo(() => {
    return [
      {
        key: 'name',
        label: t('directRoute.name'),
        visible: true,
        sortable: true
      },
      {
        key: 'numberToDial',
        label: t('directRoute.numberToDial'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: DirectRoute) => (
          <span className="font-mono font-medium text-primary">{item?.numberToDial || '-'}</span>
        )
      },
      {
        key: 'classOfService',
        label: t('directRoute.classOfService'),
        visible: true,
        sortable: false,
        render: (_value: unknown, item: DirectRoute) => (
          <span className="text-sm">{item?.classOfService?.name || '-'}</span>
        )
      },
      {
        key: 'description',
        label: t('directRoute.description'),
        visible: true,
        sortable: false
      },
      {
        key: 'enabled',
        label: t('directRoute.enabled'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: DirectRoute) => (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}>
            {item?.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        )
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false
      }
    ];
  }, [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDirectRouteList({
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    domainId: selectedDomain?.id,
  });

  // Handlers
  const handleCreate = useCallback(() => {
    navigate('/pbx/applications/direct-route/new');
  }, [navigate]);

  const handleEdit = useCallback((route: DirectRoute) => {
    navigate(`/pbx/applications/direct-route/${route.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((route: DirectRoute) => {
    navigateToDirectRouteDuplicate(navigate, String(route.id));
  }, [navigate]);

  const handleDeleteClick = useCallback((route: DirectRoute) => {
    setDeleteModal({ isOpen: true, route });
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deleteModal.route) return;

    setIsDeleting(true);
    try {
      await deleteDirectRouteById(String(deleteModal.route.id));

      showSuccess(t('directRoute.messages.deletedSuccess'));
      setDeleteModal({ isOpen: false, route: null });
      refresh();
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('directRoute.messages.deleteError');
      showError(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  // Render row actions
  const renderRowActions = useCallback((route: DirectRoute) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(route)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(route)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDeleteClick(route)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDeleteClick, handleDuplicate, handleEdit, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onDelete={handleDeleteClick}
          onColumnToggle={handleColumnToggle}
          title={t('directRoute.title')}
          titleIcon={<MapPin className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          renderActions={renderRowActions}
          emptyMessage={t('directRoute.emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, route: null })}
        onConfirm={handleDeleteConfirm}
        title={t('directRoute.deleteTitle')}
        message={t('directRoute.messages.confirmDelete', {
          name: deleteModal.route?.name || ''
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

